<script setup lang="ts">
  import { cn } from '../../../lib/utils'
  import { useFormFieldProvider } from '../gat-form-field.vue'
  import { type HTMLAttributes } from 'vue'
  import GatFormFieldLabel from '../gat-form-field-label.vue'
  import GatFormFieldMessage from '../gat-form-field-message.vue'
  import GatFormFieldAiIcon from '../gat-form-field-ai-icon.vue'

  const props = defineProps<{
    class?: HTMLAttributes['class']
    inputType?: 'textarea'
  }>()

  const emit = defineEmits<{
    (e: 'click', event: MouseEvent): void
  }>()

  const {
    fieldType,
    inputLabel,
    hasErrorMessages,
    warningMessages,
    name,
    tooltipHint,
    required,
    showOptional,
    value,
    isValid,
    isDisabled,
    isLoading,
    errorMessages,
    prependInnerIcon,
    appendInnerIcon,
    inputClass,
    messages,
    hasAiValue
  } = useFormFieldProvider()
</script>

<template>
  <div
    v-if="fieldType === 'inset'"
    :class="
      cn(
        [
          'group/field relative overflow-hidden bg-white',

          'border border-neutral-300',
          {
            'after:pointer-events-none after:absolute after:inset-0 after:z-10 after:ring-2 after:ring-inset after:content-[\'\']':
              hasErrorMessages || warningMessages.length > 0,
            'focus-within:after:ring-primary focus-within:after:pointer-events-none focus-within:after:absolute focus-within:after:inset-0 focus-within:after:z-10 focus-within:after:ring-2 focus-within:after:ring-inset focus-within:after:content-[\'\'] dark:focus-within:after:ring-blue-500':
              !hasErrorMessages,
            'after:ring-warning': warningMessages.length > 0,
            'after:ring-error': hasErrorMessages
          },
          'dark:border-white/10 dark:bg-white/5'
        ],

        props.class
      )
    "
    @click="emit('click', $event)"
  >
    <gat-form-field-label
      v-if="inputLabel"
      class="px-3"
      v-bind="{
        id: name,
        label: inputLabel,
        tooltipHint: tooltipHint,
        showOptional:
          required === true || typeof required === 'string'
            ? false
            : showOptional,
        isError: hasErrorMessages,
        isWarning: warningMessages.length > 0,
        highlightLabel: value !== undefined && value !== '' && value !== null
      }"
    >
      <template #append>
        <slot name="label-append" />
      </template>
    </gat-form-field-label>
    <div
      :class="[
        'group/input relative flex h-full w-full items-start gap-1 px-3 pb-2',
        { 'cursor-not-allowed': isDisabled }
      ]"
    >
      <slot
        v-bind="{
          isValid,
          errorMessages,
          name,
          disabled: isDisabled,
          as: fieldType,
          inputClass: cn(
            'placeholder:text-neutral-400 pt-1 placeholder:font-normal block w-full bg-transparent font-medium text-neutral-950 focus:outline-none dark:text-white dark:placeholder:text-neutral-500 sm:text-sm/6',
            {
              'border-error focus-visible:ring-error': hasErrorMessages
            },
            inputClass
          )
        }"
      >
      </slot>
      <span
        v-if="
          appendInnerIcon || isLoading || $slots['append-inner'] || hasAiValue
        "
        class="flex items-center justify-center"
      >
        <slot name="append-inner" />
        <gat-form-field-ai-icon />
      </span>
    </div>
    <gat-form-field-message>
      <slot name="messages" :messages="messages" />
      <template #message-short="props">
        <slot name="message-short" v-bind="props" />
      </template>
      <template #messages-long="props">
        <slot name="messages-long" v-bind="props" />
      </template>
    </gat-form-field-message>
  </div>
</template>
